import { SUPABASE_ANON_KEY, SUPABASE_URL } from './supabase';
import { logger } from './logger';

/**
 * Thin client for the `wp-login` / `wp-register` Edge Functions.
 *
 * We call the functions over raw fetch (not supabase.functions.invoke) so we
 * can read the HTTP status code — invoke() collapses every non-2xx into a
 * generic FunctionsHttpError and hides whether it was a 401 (wrong password)
 * vs 409 (already exists) vs 500. Those distinctions drive the UI message.
 */

export interface WpAuthResult {
  /** True when the edge function reported success. */
  ok: boolean;
  /** HTTP status (0 on a network/transport failure). */
  status: number;
  /** Human-readable error from the edge function, when ok is false. */
  error?: string;
}

async function callEdge(fn: 'wp-login' | 'wp-register', payload: Record<string, unknown>): Promise<WpAuthResult> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    logger.error(`[wpAppAuth] Supabase not configured; cannot call ${fn}`);
    return { ok: false, status: 0, error: 'Sign in is unavailable right now. Please try again later.' };
  }

  try {
    const resp = await fetch(`${SUPABASE_URL}/functions/v1/${fn}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(payload),
    });

    let data: any = null;
    try {
      data = await resp.json();
    } catch {
      // Non-JSON body (e.g. gateway error) — fall back to status text.
    }

    if (resp.ok && data?.ok) {
      return { ok: true, status: resp.status };
    }

    return {
      ok: false,
      status: resp.status,
      error: data?.error || 'Something went wrong. Please try again.',
    };
  } catch (error) {
    logger.error(`[wpAppAuth] Network error calling ${fn}:`, error);
    return { ok: false, status: 0, error: 'Network error. Please check your connection and try again.' };
  }
}

/**
 * Validate WordPress credentials and sync the Supabase password to match.
 * Call this only AFTER a direct signInWithPassword has failed. On success the
 * caller should retry signInWithPassword.
 */
export function wpLoginSync(email: string, password: string): Promise<WpAuthResult> {
  return callEdge('wp-login', { email, password });
}

/**
 * Register a new user on WordPress + Supabase with the same password.
 * On success the caller should signInWithPassword to obtain a session.
 */
export function wpRegister(params: {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
}): Promise<WpAuthResult> {
  return callEdge('wp-register', {
    email: params.email,
    password: params.password,
    first_name: params.firstName ?? '',
    last_name: params.lastName ?? '',
  });
}
